import React, { useState, useEffect } from "react";

const DiscountCalculator = () => {
  const [originalPrice, setOriginalPrice] = useState(1000);
  const [discountPercentage, setDiscountPercentage] = useState(15);
  const [quantity, setQuantity] = useState(1);
  const [discountType, setDiscountType] = useState("percentage");
  const [roundUp, setRoundUp] = useState(false);
  const [taxRate, setTaxRate] = useState(0);
  const [result, setResult] = useState({
    originalPrice: "0.00",
    discountPercentage: "0.00",
    discountAmount: "0.00",
    finalPrice: "0.00",
    perItemOriginal: "0.00",
    perItemDiscount: "0.00",
    perItemFinal: "0.00",
    discountTypeText: "",
    savingTip: "",
  });

  // Discount Calculation Function
  const calculateDiscount = () => {
    let price = parseFloat(originalPrice);
    let discount = parseFloat(discountPercentage);
    let qty = parseFloat(quantity);
    let tax = parseFloat(taxRate);

    if (isNaN(price) || price < 0 || isNaN(discount) || discount < 0 || isNaN(qty) || qty < 1) return;

    // Calculate discount amount
    let discountAmount = (price * discount) / 100;
    
    // Round up if selected
    if (roundUp) {
      discountAmount = Math.ceil(discountAmount);
    }
    
    // Calculate final price before tax
    const priceAfterDiscount = price - discountAmount;
    
    // Calculate tax
    const taxAmount = (priceAfterDiscount * tax) / 100;
    
    // Calculate final price with tax
    const finalPrice = priceAfterDiscount + taxAmount;
    
    // Calculate per item amounts
    const perItemOriginal = price / qty;
    const perItemDiscount = discountAmount / qty;
    const perItemFinal = finalPrice / qty;

    let typeText = "";
    let savingTip = "";

    switch(discountType) {
      case "clearance":
        typeText = "Clearance Sale (40-70%)";
        savingTip = "Clearance sales offer the best discounts! Perfect time to grab deals on seasonal items.";
        break;
      case "seasonal":
        typeText = "Seasonal Sale (20-40%)";
        savingTip = "Seasonal sales are great for shopping end-of-season items at reduced prices.";
        break;
      case "regular":
        typeText = "Regular Sale (10-20%)";
        savingTip = "Regular discounts are common promotions. Check for additional coupon codes for more savings.";
        break;
      case "minimal":
        typeText = "Minimal Discount (5-10%)";
        savingTip = "Small discount but still a saving! Consider combining with loyalty points or cashback.";
        break;
      case "custom":
        typeText = `Custom Discount (${discount}%)`;
        savingTip = "Custom discount applied. Always compare prices across stores before purchasing.";
        break;
      default:
        typeText = "Standard Discount";
        savingTip = "Smart shopping means knowing when to buy and when to wait for better deals.";
    }

    setResult({
      originalPrice: price.toFixed(2),
      discountPercentage: discount.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
      finalPrice: finalPrice.toFixed(2),
      perItemOriginal: perItemOriginal.toFixed(2),
      perItemDiscount: perItemDiscount.toFixed(2),
      perItemFinal: perItemFinal.toFixed(2),
      discountTypeText: typeText,
      savingTip,
    });
  };

  useEffect(() => {
  calculateDiscount();
}, [
  originalPrice,
  discountPercentage,
  quantity,
  discountType,
  roundUp,
  taxRate
]);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
          </div>
          <h1>Discount Calculator</h1>
          <p>
            Calculate discount amount and final price for shopping, sales, and bulk purchases
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Price Details
              </h2>

              {/* Original Price */}
              <div className="input-group">
                <label>Original Price</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={originalPrice}
                    onChange={(e) => setOriginalPrice(e.target.value)}
                    className="calc-input"
                    placeholder="1000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500, 1000, 2000, 5000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setOriginalPrice(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 1000 ? `${(amount / 1000).toFixed(0)}K` : amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Discount Type */}
              <div className="input-group">
                <label>Discount Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "clearance", label: "Clearance (50%)" },
                    { value: "seasonal", label: "Seasonal (30%)" },
                    { value: "regular", label: "Regular (15%)" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setDiscountType(type.value);
                        if (type.value === "clearance") setDiscountPercentage(50);
                        else if (type.value === "seasonal") setDiscountPercentage(30);
                        else if (type.value === "regular") setDiscountPercentage(15);
                      }}
                      className={`quick-select-btn ${discountType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "minimal", label: "Minimal (5%)" },
                    { value: "custom", label: "Custom %" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setDiscountType(type.value);
                        if (type.value === "minimal") setDiscountPercentage(5);
                      }}
                      className={`quick-select-btn ${discountType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Discount Percentage */}
              <div className="input-group">
                <label>Discount Percentage (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(e.target.value)}
                    className="calc-input"
                    placeholder="15"
                    step="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[10, 20, 30, 50].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setDiscountPercentage(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="input-group">
                <label>Quantity</label>
                <div className="input-wrapper">
                  <span className="input-prefix">#</span>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="calc-input"
                    placeholder="1"
                    min="1"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 2, 5, 10].map((qty) => (
                    <button
                      key={qty}
                      onClick={() => setQuantity(qty)}
                      className="quick-select-btn success"
                    >
                      {qty} {qty === 1 ? 'Item' : 'Items'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tax Rate */}
              <div className="input-group">
                <label>Tax Rate (%)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">%</span>
                  <input
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    className="calc-input"
                    placeholder="0"
                    step="0.5"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 5, 12, 18].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setTaxRate(rate)}
                      className={`quick-select-btn ${taxRate === rate ? 'success' : 'secondary'}`}
                    >
                      {rate}% GST
                    </button>
                  ))}
                </div>
              </div>

              {/* Round Up */}
              <div className="input-group">
                <label>Round Discount?</label>
                <div className="quick-select-group">
                  {[
                    { value: true, label: "Yes (Round Up)" },
                    { value: false, label: "No (Exact)" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => setRoundUp(type.value)}
                      className={`quick-select-btn ${roundUp === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateDiscount} className="calculate-btn">
                Calculate Discount
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Savings Calculation
              </h2>

              <div className="result-card">
                {/* Final Price - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Final Price (After Discount)</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="9" cy="21" r="1"></circle>
                        <circle cx="20" cy="21" r="1"></circle>
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.finalPrice).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.discountTypeText} at {result.discountPercentage}%
                  </div>
                </div>

                {/* Per Item Final */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Per Item Final Price</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.perItemFinal).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Price per item after discount
                  </div>
                </div>

                {/* Discount Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">You Save</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.discountAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Total savings at {result.discountPercentage}%
                  </div>
                </div>

                {/* Original Price */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Original Price</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.originalPrice).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Price before discount
                  </div>
                </div>

                {/* Per Item Savings */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Savings Per Item</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.perItemDiscount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Discount per item
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Price Breakdown</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Original Price</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.originalPrice).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `100%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Discount ({result.discountPercentage}%)</span>
                      <span className="breakdown-percentage">
                        -₹{Number(result.discountAmount).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${parseFloat(result.originalPrice) > 0 ? (parseFloat(result.discountAmount) / parseFloat(result.originalPrice)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Per Item Distribution */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Per Item Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Original Price Per Item</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.perItemOriginal) / parseFloat(result.perItemOriginal)) * 100).toFixed(2)}% (₹{Number(result.perItemOriginal).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `100%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Discount Per Item</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.perItemDiscount) / parseFloat(result.perItemOriginal)) * 100).toFixed(2)}% (₹{Number(result.perItemDiscount).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.perItemDiscount) / parseFloat(result.perItemOriginal)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Shopping Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Smart Shopping Tips</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.savingTip} Check return policies and compare prices before making large purchases.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Discounts</h3>
              <p>
                Discounts are price reductions offered by retailers to encourage purchases and clear inventory. Common 
                discount periods include seasonal sales (end-of-season), clearance sales (50-70% off), festival sales 
                (Diwali, Christmas), and flash sales (limited time offers). A 20% discount means you pay 80% of the 
                original price. Always check if the discount applies to the MRP or a marked-up price. Compare prices 
                across multiple stores and online platforms before purchasing. Watch out for "fake discounts" where 
                prices are inflated before applying discounts. Read the fine print - some discounts exclude certain 
                products or have minimum purchase requirements. Bulk buying at discounted prices only saves money if 
                you'll actually use the items. Online shopping often offers better discounts but factor in shipping 
                costs. Use price tracking tools to verify if a discount is genuine. Store credit cards and loyalty 
                programs can provide additional discounts. Black Friday, Cyber Monday, and end-of-season sales typically 
                offer the best discounts ranging from 40-70% off.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Discount Calculation Formula:</h4>
            <div className="formula-display">
              Final Price = Original Price - (Original Price × Discount Percentage / 100)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Discount Amount</div>
                <div className="formula-param-value">Original Price × Discount % / 100</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Savings Percentage</div>
                <div className="formula-param-value">(Discount Amount / Original Price) × 100</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Clearance Sale</div>
                <div className="formula-param-value">40-70% off original price</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Seasonal Sale</div>
                <div className="formula-param-value">20-40% off marked price</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How do I calculate discount percentage?</h5>
              <p>
                Discount % = (Discount Amount / Original Price) × 100. For example, if an item costs ₹1000 and you save ₹200, the discount is 20%.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between discount and cashback?</h5>
              <p>
                Discounts reduce the price at purchase, while cashback returns money after the transaction. Discounts provide immediate savings, cashback may take days or weeks.
              </p>
            </div>

            <div className="faq-item">
              <h5>Are discounts calculated on MRP or selling price?</h5>
              <p>
                Check carefully - some stores show discounts on MRP while already selling below MRP. Always verify the base price before calculating actual savings.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I combine multiple discounts?</h5>
              <p>
                Usually not. Most stores don't allow stacking discounts. However, you might combine a sale discount with a credit card offer or loyalty points - check store policy.
              </p>
            </div>

            <div className="faq-item">
              <h5>When are the best times to find discounts?</h5>
              <p>
                End-of-season sales, festival sales (Diwali, New Year), Black Friday, Cyber Monday, and clearance events offer the deepest discounts ranging from 40-70% off.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I verify if a discount is genuine?</h5>
              <p>
                Use price tracking websites, check price history, compare across multiple retailers, and verify the pre-discount price wasn't artificially inflated just before the sale.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountCalculator;